import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "@mui/material";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { Box } from "@mui/system";
import { useUpdateUserOrgStatusMutation } from "state/rtkQuery/user/userMutations";

import ConfirmationModal from "../Modals/ConfirmationModal/ConfirmationModal";
import RemoveUserConfirmationModal from "../Modals/RemoveUserConfirmationModal/RemoveUserConfirmationModal";

import "./UserActions.css";

const UserActions = ({
  params,
  refetchOrgStatus,
  hasJoinedOrg,
  hasAnyOrganizationsOnPlatform,
}) => {
  const navigate = useNavigate();
  const isLicenseLimited = process.env.REACT_APP_GITHUB_LICENSE_LIMITED === "true";
  const licenseTooltip = "Users are temporarily not allowed to request access to this organization due to GitHub license limitations.";

  // Or from Redux/context if needed
  // Local states
  const [showModal, setShowModal] = useState(false);
  const [requestReason, setRequestReason] = useState("");
  const [dtProjectNeed, setDtProjectNeed] = useState("");
  const [dtMemberFirmDetails, setDtMemberFirmDetails] = useState("");
  const [dtProjectLead, setDtProjectLead] = useState("");
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [permissions, setPermissions] = useState([]);

  const userPermissions = useSelector((state) => state.auth.permissions);

  useEffect(() => {
    if (userPermissions && params) {
      setPermissions(
        userPermissions.find((permission) => permission.orgId === params.row.id)
      );
    }
  }, [userPermissions, params]);
  // RTK Query for updating user organization status
  const [
    updateUserOrgStatus,
    {
      data: updateUserOrgResponseData,
      isLoading: isUpdateUserOrgLoading,
      isSuccess: isUpdateUserOrgSuccess,
      error: updateUserOrgError,
    },
  ] = useUpdateUserOrgStatusMutation();

  // Update user organization status api call function
  const onAddUserToOrganizationHandler = () => {
    const reason =
      params.row.approvalRequired === "Yes" && isDTGlobalCollab
        ? `Project Need: ${dtProjectNeed}; Member Firm Details: ${dtMemberFirmDetails}; Project Lead: ${dtProjectLead}`
        : requestReason;

    // Close modal
    handleClose();
    // Update user organization status
    updateUserOrgStatus({
      userId: params.row.userId,
      body: {
        platforms: {
          [params.row.platformId]: {
            organizations: {
              [params.row.id]: {
                membership_status:
                  params.row.approvalRequired === "Yes"
                    ? "Pending"
                    : "Requested",
                reason,
                changed_by: params.row.email.toLowerCase(),
              },
            },
          },
        },
      },
      attribute: "membership_status",
    });
  };

  // Close modal function
  const handleClose = (event, reason) => {
    if (reason === "backdropClick") {
    } else {
      setShowModal(false);
      // Reset request reason on modal close
      setRequestReason("");
      setDtProjectNeed("");
      setDtMemberFirmDetails("");
      setDtProjectLead("");
    }
  };

  // Open modal function
  const openModal = () => {
    setShowModal(true);
  };

  // Navigates to admin dashboard
  const viewRequests = () => {
    navigate(`/admin/${params.row.id}/dashboard`);
  };

  // DT-Global-Collaboration org logic
  const normalizedOrgDisplayName = (params.row.orgDisplayName || "")
    .toUpperCase()
    .replace(/\s+/g, "-");
  const normalizedOrgName = (params.row.orgName || "")
    .toUpperCase()
    .replace(/\s+/g, "-");
  const isDTGlobalCollab =
    normalizedOrgDisplayName === "DT-GLOBAL-COLLABORATION" ||
    normalizedOrgName === "DT-GLOBAL-COLLABORATION";
  const canRequestDTGlobal = isDTGlobalCollab
    ? !!hasAnyOrganizationsOnPlatform
    : hasJoinedOrg;
  const showDTGlobalTooltip = isDTGlobalCollab && !canRequestDTGlobal;
  const dtGlobalTooltip = "You must be part of a member firm org first in order to request access to this org.";

  return (
    <div>
      {/* Confirmation modal component */}
      {showModal && (
        <ConfirmationModal
          show={showModal}
          close={handleClose}
          params={params.row}
          onAddOrganizationHandler={onAddUserToOrganizationHandler}
          isDTGlobalCollab={isDTGlobalCollab}
          dtProjectNeed={dtProjectNeed}
          setDtProjectNeed={setDtProjectNeed}
          dtMemberFirmDetails={dtMemberFirmDetails}
          setDtMemberFirmDetails={setDtMemberFirmDetails}
          dtProjectLead={dtProjectLead}
          setDtProjectLead={setDtProjectLead}
          requestReason={requestReason}
          setRequestReason={setRequestReason}
        />
      )}
      <Box sx={{ m: 1, position: "relative" }}>
        {/* Request Access Button */}
        {permissions?.userType !== "Owner" && (
          <>
            {/* Show "Remove" button if user is joined in a removable org */}
            {params.row.status === "Joined" &&
              params.row.user_removal_enabled &&
              params.row.userType === "User" ? (
              <Button
                variant="outlined"
                color="error"
                sx={{
                  minWidth: "9rem",
                  padding: "5px",
                  margin: "5px",
                }}
                className="remove-request-btn"
                onClick={() => setShowRemoveModal(true)}
                disabled={isUpdateUserOrgLoading}
              >
                {isUpdateUserOrgLoading ? (
                  <>
                    <CircularProgress
                      size={14}
                      sx={{ color: "#d32f2f !important" }}
                    />
                    <span style={{ marginLeft: "5px" }}>Removing...</span>
                  </>
                ) : (
                  "Leave Organization"
                )}
              </Button>
            ) : params.row.status === "Remove-Requested" ||
              params.row.status === "Removed" ? (
              <Tooltip
                title={
                  <span>
                    Your account has been removed from this organization.
                    <br />
                    Please raise a{" "}
                    <a
                      href="https://deloitteglobal.service-now.com/sp?id=sc_cat_item&sys_id=4a417b4f1b17855cbcae55b0604bcb1c"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: "#e9e9e9ff",
                        textDecoration: "underline",
                      }}
                    >
                      <span style={{ fontWeight: "600" }}>ticket</span>
                    </a>{" "}
                    to reset your profile, in order to request again.
                  </span>
                }
                arrow
                placement="top"
                componentsProps={{
                  tooltip: {
                    sx: {
                      fontSize: "1rem", // Set your desired font size
                      padding: "12px",
                      width: "400px",
                    },
                  },
                }}
              >
                <span>
                  <Button
                    variant="outlined"
                    sx={{
                      minWidth: "9rem",
                      padding: "5px",
                      margin: "5px",
                      color: "#d32f2f",
                      borderColor: "#d32f2f",
                    }}
                    disabled
                  >
                    Removed
                  </Button>
                </span>
              </Tooltip>
            ) : (
              // Default logic for other statuses
              <Tooltip
                title={
                  showDTGlobalTooltip
                    ? dtGlobalTooltip
                    : isLicenseLimited && !hasJoinedOrg
                    ? licenseTooltip
                    : ""
                }
                arrow
                disableHoverListener={!(showDTGlobalTooltip || (isLicenseLimited && !hasJoinedOrg))}
                placement="top"
                componentsProps={{
                  tooltip: {
                    sx: {
                      fontSize: "1rem",
                      padding: "12px",
                      width: "400px",
                    },
                  },
                }}
              >
                <span>
                  <Button
                    variant="contained"
                    sx={{
                      minWidth: "9rem",
                      padding: "5px",
                      margin: "5px",
                      cursor:
                        showDTGlobalTooltip || (isLicenseLimited && !hasJoinedOrg)
                          ? "not-allowed"
                          : "pointer",
                      opacity:
                        showDTGlobalTooltip || (isLicenseLimited && !hasJoinedOrg)
                          ? 0.7
                          : 1,
                    }}
                    className="make-request-btn"
                    onClick={() => openModal()}
                    disabled={
                      (showDTGlobalTooltip || (!hasJoinedOrg && isLicenseLimited)) || params.row.action !== "Add"
                    }
                  >
                    {isUpdateUserOrgLoading ? (
                      <>
                        <CircularProgress size={14} />
                        <span style={{ marginLeft: "5px" }}>Requesting...</span>
                      </>
                    ) : params.row.status == "In-Progress" ? (
                      "In Progress"
                    ) : params.row.status == "Pending" ? (
                      "Approval Pending"
                    ) : params.row.status == "Joined" ? (
                      "Joined"
                    ) : params.row.status == "Failed" ? (
                      "Resend Request"
                    ) : params.row.status == "Rejected" ? (
                      "Rejected"
                    ) : params.row.status == "Removed" ? (
                      "Removed"
                    ) : params.row.approvalRequired == "Yes" ? (
                      "Request Access"
                    ) : (
                      "Join"
                    )}
                  </Button>
                </span>
              </Tooltip>
            )}
          </>
        )}
        {/* View Requests Button */}
        {(params.row.userType === "Owner" ||
          params.row.userType === "Approver") && (
            <Button
              variant="outlined"
              sx={{
                minWidth: "9rem",
                margin: "5px",
                padding: "5px",
              }}
              className="viewRequestButton"
              onClick={() => viewRequests()}
            >
              View Requests
            </Button>
          )}
      </Box>
      <RemoveUserConfirmationModal
        show={showRemoveModal}
        close={() => setShowRemoveModal(false)}
        orgName={params.row.orgDisplayName}
        userId={params.row.userId}
        platformId={params.row.platformId}
        orgId={params.row.id}
        email={params.row.email}
        updateUserOrgStatus={updateUserOrgStatus}
        isLoading={isUpdateUserOrgLoading}
        refetchOrgStatus={refetchOrgStatus} // If you have a refetch function
      />
    </div>
  );
};

export default UserActions;
